import { useState, useEffect } from "react";
import {
  Box, Typography, Select, MenuItem, FormControl, InputLabel,
  Button, Stack, Chip, CircularProgress,
} from "@mui/material";
import RouterIcon from "@mui/icons-material/Router";
import API from "../../../api";
import { startToast, updateToastError, updateToastSuccess } from "../../../utils/notification-loading";

const ResponseIfaceCard = () => {
  const [options,  setOptions]  = useState([]);   // { label, value }[]
  const [current,  setCurrent]  = useState(null); // confirmed by backend
  const [draft,    setDraft]    = useState("");    // selected in UI
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([
      API.getDeviceInfo(),
      API.getConfig({ type: "response-iface" }),
    ])
      .then(([deviceRes, configRes]) => {
        const info   = deviceRes.data ?? {};
        const ifaces = info?.interface ?? [];
        setOptions(
          ifaces.map((iface, i) => ({
            label: `LAN ${i + 1}${iface.ip ? ` — ${iface.ip}` : ""}${iface.status_up ? "" : " (DOWN)"}`,
            value: iface.name,
          }))
        );
        const saved = configRes.data?.config ?? null;
        setCurrent(saved);
        setDraft(saved ?? "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const isDirty = draft !== (current ?? "");

  const handleSave = () => {
    setLoading(true);
    startToast();
    API.setResponseIface({ iface: draft || null })
      .then((res) => {
        const confirmed = res?.data?.data?.response_iface ?? (draft || null);
        setCurrent(confirmed);
        setDraft(confirmed ?? "");
        updateToastSuccess(confirmed ? `Response interface set: ${confirmed}` : "Reset to capture interface");
      })
      .catch(() => updateToastError("Failed to set response interface"))
      .finally(() => setLoading(false));
  };

  const handleReset = () => {
    setLoading(true);
    startToast();
    API.resetResponseIface()
      .then(() => {
        setCurrent(null);
        setDraft("");
        updateToastSuccess("Response interface reset to capture interface");
      })
      .catch(() => updateToastError("Failed to reset response interface"))
      .finally(() => setLoading(false));
  };

  const currentLabel = options.find((o) => o.value === current)?.label ?? current;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Typography sx={{ fontWeight: 600, color: "#e8e8e8" }}>
          Response Interface
        </Typography>
        {current ? (
          <Chip
            icon={<RouterIcon sx={{ fontSize: 13 }} />}
            label={currentLabel}
            size="small"
            sx={{
              height: 20, fontSize: "0.68rem", fontWeight: 700,
              backgroundColor: "rgba(69,144,149,0.18)", color: "#43B4B9",
              border: "1px solid rgba(69,144,149,0.4)",
            }}
          />
        ) : (
          <Chip
            label="using capture iface"
            size="small"
            sx={{ height: 20, fontSize: "0.68rem", backgroundColor: "transparent", color: "#555", border: "1px solid #333" }}
          />
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        By default, countermeasures are applied on the same interface used for traffic capture.
        Select a dedicated LAN port to use a separate interface for response actions.
      </Typography>

      <Stack direction="row" spacing={1.5} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 260 }} disabled={loading}>
          <InputLabel>Interface</InputLabel>
          <Select
            label="Interface"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          >
            <MenuItem value="">
              <em style={{ color: "#666" }}>— use capture interface —</em>
            </MenuItem>
            {options.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained" size="small"
          onClick={handleSave}
          disabled={loading || !isDirty}
          sx={{
            backgroundColor: "#449095",
            "&:hover": { backgroundColor: "#367a7e" },
            "&.Mui-disabled": { backgroundColor: "#2a2a2a", color: "#555" },
          }}
        >
          {loading
            ? <CircularProgress size={14} sx={{ color: "#fff" }} />
            : "Apply"}
        </Button>

        <Button
          variant="outlined" size="small"
          onClick={handleReset}
          disabled={loading || !current}
          sx={{
            borderColor: "#c0392b55", color: "#c0392b",
            "&:hover": { borderColor: "#c0392b", backgroundColor: "#c0392b11" },
            "&.Mui-disabled": { borderColor: "#333", color: "#444" },
          }}
        >
          Reset
        </Button>
      </Stack>
    </Box>
  );
};

export default ResponseIfaceCard;
